// Time view — hours + billable $ by agent / ticket / sub-account, plus SLA tiles.
// GET /time/report?range=&groupBy=. Charts stay lightweight (StatTile + BarList).
import { useEffect, useState } from 'react';
import type { TimeReport as TimeReportData } from '../../lib/suite-types';
import { agencyApi } from '../../lib/agencyApi';
import { BarList, Card, Chip, EmptyNote, ErrorNote, SectionHeader, Select, Spinner, StatTile } from './ui';

// The report may carry optional SLA aggregates the Worker computes from
// first_response_at / resolved_at. Read defensively — the base type omits them.
type SlaBlock = { avgFirstResponseHours?: number | null; avgResolutionHours?: number | null } | null;

// The Worker also folds time-integrity aggregates in from ss_time_activity: a
// top-level flagged-entry count and/or per-row flag data. Read defensively —
// the base TimeReport type doesn't list them.
type FlagAgg = { flaggedEntries?: number };
type FlaggedRow = TimeReportData['rows'][number] & { flaggedEntries?: number; flags?: string[] };

// How many flagged entries a row represents (prefers an explicit count; else
// treats a row carrying any flags as one flagged group).
function rowFlaggedCount(r: TimeReportData['rows'][number]): number {
  const fr = r as FlaggedRow;
  if (typeof fr.flaggedEntries === 'number') return fr.flaggedEntries;
  return (fr.flags?.length ?? 0) > 0 ? 1 : 0;
}

const GROUPS: [TimeReportData['groupBy'], string][] = [
  ['agent', 'Agent'],
  ['ticket', 'Ticket'],
  ['subaccount', 'Sub-account'],
];

const RANGES: [string, string][] = [
  ['7d', 'Last 7 days'],
  ['30d', 'Last 30 days'],
  ['90d', 'Last 90 days'],
  ['all', 'All time'],
];

const hours = (seconds: number) => (seconds / 3600).toFixed(1);
const dollars = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export default function TimeReport() {
  const [groupBy, setGroupBy] = useState<TimeReportData['groupBy']>('agent');
  const [range, setRange] = useState('30d');
  const [report, setReport] = useState<TimeReportData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setReport(null);
    setError(null);
    agencyApi
      .timeReport({ range, groupBy })
      .then(setReport)
      .catch((e: Error) => setError(e.message));
  }, [range, groupBy]);

  const sla = report ? ((report as { sla?: SlaBlock }).sla ?? null) : null;

  // Flagged-entry total: prefer the report-level count, else sum per-row flag data.
  const flaggedCount = report
    ? (() => {
        const top = (report as FlagAgg).flaggedEntries;
        if (typeof top === 'number') return top;
        const sum = report.rows.reduce((s, r) => s + rowFlaggedCount(r), 0);
        return sum > 0 ? sum : null;
      })()
    : null;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Time & billing"
        subtitle="Tracked hours and billable value across your team, tickets, and sub-accounts."
        action={
          <div className="flex items-center gap-2">
            <Select value={groupBy} onChange={(v) => setGroupBy(v as TimeReportData['groupBy'])}>
              {GROUPS.map(([id, label]) => (
                <option key={id} value={id}>By {label.toLowerCase()}</option>
              ))}
            </Select>
            <Select value={range} onChange={setRange}>
              {RANGES.map(([id, label]) => (
                <option key={id} value={id}>{label}</option>
              ))}
            </Select>
          </div>
        }
      />

      {error && <ErrorNote error={error} />}
      {!error && !report && <Spinner label="Loading time report…" />}

      {report && report.rows.length === 0 && (
        <EmptyNote>
          No time tracked in this range yet. Time is logged when your team works tickets in the Help
          Desk — start a timer on a ticket (or connect a tracker in Settings) and hours &amp; billable value appear here.
        </EmptyNote>
      )}

      {report && report.rows.length > 0 && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatTile label="Total hours" value={hours(report.totals.totalSeconds)} />
            <StatTile
              label="Billable hours"
              value={hours(report.totals.billableSeconds)}
              hint={`${dollars(report.totals.billableCents)} value`}
            />
            <StatTile label="Billable $" value={dollars(report.totals.billableCents)} />
            {sla?.avgFirstResponseHours != null ? (
              <StatTile label="Avg first response" value={`${sla.avgFirstResponseHours.toFixed(1)}h`} />
            ) : (
              <StatTile
                label="Entries"
                value={report.rows.reduce((s, r) => s + r.entries, 0)}
              />
            )}
            {flaggedCount != null && (
              <StatTile
                label="Flagged entries"
                value={flaggedCount}
                hint="clocks left running / off-account time"
              />
            )}
          </div>

          {sla && (sla.avgFirstResponseHours != null || sla.avgResolutionHours != null) && (
            <div className="grid grid-cols-2 gap-4">
              <StatTile
                label="Avg first response"
                value={sla.avgFirstResponseHours != null ? `${sla.avgFirstResponseHours.toFixed(1)}h` : '—'}
              />
              <StatTile
                label="Avg resolution"
                value={sla.avgResolutionHours != null ? `${sla.avgResolutionHours.toFixed(1)}h` : '—'}
              />
            </div>
          )}

          <Card className="p-5">
            <h3 className="mb-4 text-sm font-semibold text-slate-700">
              Hours by {GROUPS.find(([id]) => id === groupBy)?.[1].toLowerCase()}
            </h3>
            <BarList
              items={report.rows.map((r) => ({
                label: r.label,
                value: Number(hours(r.totalSeconds)),
              }))}
              emptyLabel="No time tracked in this range."
            />
          </Card>

          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
                    <th className="px-5 py-3 font-medium">
                      {GROUPS.find(([id]) => id === groupBy)?.[1]}
                    </th>
                    <th className="px-5 py-3 text-right font-medium">Entries</th>
                    <th className="px-5 py-3 text-right font-medium">Hours</th>
                    <th className="px-5 py-3 text-right font-medium">Billable hrs</th>
                    <th className="px-5 py-3 text-right font-medium">Billable $</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {report.rows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-5 py-8 text-center text-slate-400">
                        No time tracked in this range.
                      </td>
                    </tr>
                  ) : (
                    report.rows.map((r) => (
                      <tr key={r.key}>
                        <td className="px-5 py-3 font-medium text-slate-700">
                          <span className="inline-flex items-center gap-2">
                            {r.label}
                            {rowFlaggedCount(r) > 0 && <Chip label="⚠ flagged" color="#d97706" />}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right tabular-nums text-slate-600">{r.entries}</td>
                        <td className="px-5 py-3 text-right tabular-nums text-slate-700">{hours(r.totalSeconds)}</td>
                        <td className="px-5 py-3 text-right tabular-nums text-slate-700">{hours(r.billableSeconds)}</td>
                        <td className="px-5 py-3 text-right tabular-nums font-medium text-slate-900">{dollars(r.billableCents)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                {report.rows.length > 0 && (
                  <tfoot>
                    <tr className="border-t border-slate-200 font-semibold text-slate-900">
                      <td className="px-5 py-3">Total</td>
                      <td className="px-5 py-3 text-right tabular-nums">
                        {report.rows.reduce((s, r) => s + r.entries, 0)}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums">{hours(report.totals.totalSeconds)}</td>
                      <td className="px-5 py-3 text-right tabular-nums">{hours(report.totals.billableSeconds)}</td>
                      <td className="px-5 py-3 text-right tabular-nums">{dollars(report.totals.billableCents)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
